import { IconPlus } from "@tabler/icons-react";
import { useState, type FormEvent } from "react";
import { api, errorText } from "../api";
import { dateTime, initials } from "../format";
import type { Organization, PublicUser, RoleNames, ScopedResource, UserMembership } from "../types";
import { Checkbox } from "../checkbox";
import { useConfirm, useToast } from "../feedback";
import { Select } from "../select";
import { Badge, Banner, Field, PageHead, Panel, useAsync, type Notice } from "../ui";

export interface UsersPageProps {
  /** The signed-in user, who cannot deactivate themselves. */
  me: { id: string };
  roleNames: RoleNames;
  /** Roles offered for an organization, in order; e.g. operator roles for the operator. */
  rolesFor: (org: Organization) => string[];
  /** Resources a partner membership can be limited to. */
  resources: ScopedResource[];
  /** Membership field with the resource IDs, as in the app's access model (e.g. "propertyIds"). */
  resourceKey: string;
  /** Plural noun for the resources, e.g. "Properties". */
  resourceLabel: string;
}

const resourcesOf = (m: UserMembership, key: string) => ((m as unknown as Record<string, unknown>)[key] as string[] | undefined) ?? [];

/** Who can enter the extranet and what they can do. Only for operator administrators. */
export function UsersPage(props: UsersPageProps) {
  const { me, roleNames, resources, resourceKey } = props;
  const { data, error, reload } = useAsync(() => Promise.all([
    api<{ users: PublicUser[] }>("GET", "/users").then((r) => r.users),
    api<{ organizations: Organization[] }>("GET", "/organizations").then((r) => r.organizations),
  ]), []);
  const [notice, setNotice] = useState<Notice>();
  const [creating, setCreating] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  const [users, orgs] = data ?? [[], []];
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name ?? "?";
  const resourceName = (id: string) => resources.find((p) => p.id === id)?.name ?? "?";

  const act = async (fn: () => Promise<{ temporaryPassword?: string } | unknown>, done: (r: { temporaryPassword?: string }) => string) => {
    try { const r = (await fn()) as { temporaryPassword?: string }; setNotice({ tone: "ok", text: done(r ?? {}) }); reload(); } catch (err) { setNotice({ tone: "bad", text: errorText(err) }); }
  };

  return (
    <>
      <PageHead title="Users" sub="Who can sign in to the extranet and what they can do"><button className="primary" onClick={() => setCreating(true)}><IconPlus size={16} /> New user</button></PageHead>
      <Banner notice={notice} onClose={() => setNotice(undefined)} />
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      <div className={creating ? "with-panel" : ""}>
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>User</th><th>Access</th><th>Last sign-in</th><th /></tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id}>
                <td><div className="row" style={{ flexWrap: "nowrap" }}><div className="avatar">{initials(u.name)}</div><div><div style={{ fontWeight: 500 }}>{u.name} {u.status !== "active" && <Badge>inactive</Badge>}</div><div className="small muted">{u.email}</div></div></div></td>
                <td>{u.memberships.map((m, i) => <div key={i}>{roleNames[m.role]} · {orgName(m.organizationId)}{resourcesOf(m, resourceKey).length > 0 && <div className="small muted">{resourcesOf(m, resourceKey).map(resourceName).join(", ")}</div>}</div>)}</td>
                <td className="muted">{u.lastLoginAt ? dateTime(u.lastLoginAt) : "never"}</td>
                <td className="num">{u.id === me.id ? <span className="faint">you</span> : (
                  <div className="row" style={{ justifyContent: "flex-end" }}>
                    <button onClick={async () => {
                      if (u.status === "active" && !(await confirm({ title: `Deactivate ${u.email}?`, body: "Their open sessions will be signed out.", confirmLabel: "Deactivate", danger: true }))) return;
                      try {
                        await api("PUT", `/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" });
                        toast(u.status === "active" ? `${u.email} deactivated.` : `${u.email} activated.`);
                        reload();
                      } catch (err) { setNotice({ tone: "bad", text: errorText(err) }); }
                    }}>{u.status === "active" ? "Deactivate" : "Activate"}</button>
                    {/* The new password stays in a banner: it is shown only once and has to be copied. */}
                    <button onClick={async () => { if (await confirm({ title: `Generate a new password for ${u.email}?`, body: "The current one will stop working.", confirmLabel: "Generate" })) void act(() => api("POST", `/users/${u.id}/reset-password`), (r) => `New temporary password for ${u.email} (shown only now): ${r.temporaryPassword}`); }}>New password</button>
                  </div>
                )}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {creating && <NewUser {...props} orgs={orgs} onClose={() => setCreating(false)} onCreated={(text) => { setCreating(false); setNotice({ tone: "ok", text }); reload(); }} />}
      </div>
    </>
  );
}

function NewUser({ orgs, roleNames, rolesFor, resources, resourceKey, resourceLabel, onClose, onCreated }: UsersPageProps & { orgs: Organization[]; onClose: () => void; onCreated: (text: string) => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [orgId, setOrgId] = useState(orgs[0]?.id ?? "");
  const org = orgs.find((o) => o.id === orgId);
  const roles = org ? rolesFor(org) : [];
  const [role, setRole] = useState(roles[0] ?? "");
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const orgResources = resources.filter((p) => p.organizationId === orgId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    try {
      const r = await api<{ temporaryPassword: string }>("POST", "/users", { email, name, memberships: [{ organizationId: orgId, role: roles.includes(role) ? role : roles[0], [resourceKey]: org?.kind === "operator" ? [] : picked }] });
      onCreated(`User created. Temporary password (shown only now): ${r.temporaryPassword}\nShare it over a secure channel; the user should change it at first sign-in.`);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Panel title="New user" onClose={onClose}>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" /></Field>
        <Field label="Name"><input required value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Organization"><Select value={orgId} onChange={(v) => { setOrgId(v); setPicked([]); }} options={orgs.map((o) => ({ value: o.id, label: `${o.name}${o.kind === "operator" ? " (operator)" : ""}` }))} /></Field>
        <Field label="Role"><Select value={roles.includes(role) ? role : (roles[0] ?? "")} onChange={(v) => setRole(v as typeof role)} options={roles.map((r) => ({ value: r, label: roleNames[r] }))} /></Field>
        {org?.kind !== "operator" && (
          <div><div className="small muted" style={{ marginBottom: 4 }}>{resourceLabel} (none = all in the organization)</div>
            <div className="stack" style={{ gap: 6 }}>{orgResources.map((p) => <Checkbox key={p.id} label={p.name} checked={picked.includes(p.id)} onCheckedChange={(on) => setPicked(on ? [...picked, p.id] : picked.filter((x) => x !== p.id))} />)}</div>
          </div>
        )}
        <div className="row"><button className="primary" type="submit">Create user</button><button type="button" onClick={onClose}>Cancel</button></div>
      </form>
    </Panel>
  );
}
